import { useState } from 'react';
import { Search, X, SlidersHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useFilterStore } from '@/stores/useFilterStore';
import type { ResponseTier } from '@/types/api.types';
import { TIER_LABELS, TIER_COLORS } from '../utils/tierColors';

type UserAction = 'Pending' | 'Accepted' | 'Modified' | 'Rejected';
type SortOption = 'index' | 'confidence-desc' | 'confidence-asc' | 'tier';
type QuickFilter = 'all' | 'to-review' | 'reviewed';

const TIERS: ResponseTier[] = ['Exact', 'Suggested', 'Generated', 'NoMatch'];
const STATUSES: UserAction[] = ['Pending', 'Accepted', 'Modified', 'Rejected'];

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: 'index', label: 'Question Order' },
  { value: 'confidence-desc', label: 'Confidence (High to Low)' },
  { value: 'confidence-asc', label: 'Confidence (Low to High)' },
  { value: 'tier', label: 'Response Tier' },
];

const STATUS_COLORS: Record<UserAction, string> = {
  Pending: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
  Accepted: 'bg-green-100 text-green-700 hover:bg-green-200',
  Modified: 'bg-blue-100 text-blue-700 hover:bg-blue-200',
  Rejected: 'bg-red-100 text-red-700 hover:bg-red-200',
};

export interface ReviewFilterBarProps {
  className?: string;
}

export function ReviewFilterBar({ className }: ReviewFilterBarProps) {
  const {
    selectedTiers,
    selectedStatuses,
    searchQuery,
    sortBy,
    toggleTier,
    toggleStatus,
    setSelectedStatuses,
    setSearchQuery,
    setSortBy,
    resetFilters,
  } = useFilterStore();

  const [filtersOpen, setFiltersOpen] = useState(false);

  // Only count tier filters in badge — status filters are visible in the quick toggle
  const advancedFilterCount = selectedTiers.length;

  // Determine quick filter state
  const getQuickFilter = (): QuickFilter => {
    if (selectedStatuses.length === 1 && selectedStatuses[0] === 'Pending' && selectedTiers.length === 0) {
      return 'to-review';
    }
    if (
      selectedStatuses.length === 3 &&
      selectedStatuses.includes('Accepted') &&
      selectedStatuses.includes('Modified') &&
      selectedStatuses.includes('Rejected') &&
      selectedTiers.length === 0
    ) {
      return 'reviewed';
    }
    if (selectedStatuses.length === 0 && selectedTiers.length === 0) {
      return 'all';
    }
    return 'all'; // fallback
  };

  const handleQuickFilter = (filter: QuickFilter) => {
    if (filter === 'all') {
      resetFilters();
    } else if (filter === 'to-review') {
      setSelectedStatuses(['Pending']);
    } else if (filter === 'reviewed') {
      setSelectedStatuses(['Accepted', 'Modified', 'Rejected']);
    }
  };

  const quickFilter = getQuickFilter();

  return (
    <div className={cn('flex items-center gap-3 flex-wrap', className)}>
      {/* Search */}
      <div className="relative flex-1 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          type="text"
          placeholder="Search questions or responses..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9 bg-background text-foreground placeholder:text-muted-foreground border-input"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Sort */}
      <select
        value={sortBy}
        onChange={(e) => setSortBy(e.target.value as SortOption)}
        className="text-sm border rounded-md px-2 py-1.5 bg-background h-9"
      >
        {SORT_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      {/* Quick Toggle: All | To Review | Reviewed */}
      <div className="flex items-center border rounded-md">
        {(['all', 'to-review', 'reviewed'] as QuickFilter[]).map((filter) => {
          const label = filter === 'all' ? 'All' : filter === 'to-review' ? 'To Review' : 'Reviewed';
          return (
            <Button
              key={filter}
              variant={quickFilter === filter ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => handleQuickFilter(filter)}
              className={cn(
                filter === 'all' && 'rounded-r-none',
                filter === 'reviewed' && 'rounded-l-none',
                filter === 'to-review' && 'rounded-none border-x'
              )}
            >
              {label}
            </Button>
          );
        })}
      </div>

      {/* More Filters Popover */}
      <Popover open={filtersOpen} onOpenChange={setFiltersOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="gap-1.5">
            <SlidersHorizontal className="h-4 w-4" />
            More Filters
            {advancedFilterCount > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 w-5 p-0 justify-center text-xs">
                {advancedFilterCount}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72" align="end">
          <div className="space-y-4">
            {/* Tier filters */}
            <div className="space-y-2">
              <span className="text-sm font-medium">Answer Type</span>
              <div className="flex items-center gap-1.5 flex-wrap">
                {TIERS.map((tier) => {
                  const isSelected = selectedTiers.includes(tier);
                  const colors = TIER_COLORS[tier];
                  return (
                    <button
                      key={tier}
                      type="button"
                      onClick={() => toggleTier(tier)}
                      className={cn(
                        'px-2 py-1 text-xs rounded-md transition-colors',
                        isSelected
                          ? `${colors.bg} text-white`
                          : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      )}
                    >
                      {TIER_LABELS[tier]}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Status filters */}
            <div className="space-y-2">
              <span className="text-sm font-medium">Review Status</span>
              <div className="flex items-center gap-1.5 flex-wrap">
                {STATUSES.map((status) => {
                  const isSelected = selectedStatuses.includes(status);
                  return (
                    <button
                      key={status}
                      type="button"
                      onClick={() => toggleStatus(status)}
                      className={cn(
                        'px-2 py-1 text-xs rounded-md transition-colors',
                        isSelected
                          ? STATUS_COLORS[status]
                          : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      )}
                    >
                      {status}
                    </button>
                  );
                })}
              </div>
            </div>

            {advancedFilterCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={resetFilters}
                className="w-full text-xs"
              >
                Clear all filters
              </Button>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
